import React, { useState, useEffect } from 'react';
import { Line, Pie } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  ArcElement,
  Title, 
  Tooltip, 
  Legend
} from 'chart.js';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import IconButton from '@mui/material/IconButton';
import { Modal, Box, Typography, Button, CircularProgress, FormControl, RadioGroup, FormControlLabel, Radio, Divider } from '@mui/material';
import DatePicker from '@mui/lab/DatePicker';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import LEDSSLogo from '../assets/LED-Speed-Signs-Logo.png';

import './Dashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const DataDashboard = () => {
  const navigate = useNavigate();
  const [loading1, setLoading1] = useState(true);
  const [loading2, setLoading2] = useState(true);
  const [loading3, setLoading3] = useState(true);
  const [primaryColor, setPrimaryColor] = useState(localStorage.getItem('primaryColor') || '#0f0f0f');
  const [secondaryColor, setSecondaryColor] = useState(localStorage.getItem('secondaryColor') || '#1a1a1a');
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');

  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [allDevicesSelected, setAllDevicesSelected] = useState(false);

  const [error, setError] = useState(null);
  
  const [timeRange, setTimeRange] = useState('daily');
  const [selectedDevice, setSelectedDevice] = useState('all');
  const [chartData, setChartData] = useState([]);

  const [operatingStatus, setOperatingStatus] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const [packageAllowance, setPackageAllowance] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportName, setExportName] = useState('');
  const [exportFormat, setExportFormat] = useState('csv');



  useEffect(() => {
    fetchPackageAllowance();
    fetchRecordSummaries();
  }, []);

  useEffect(() => {
  // Format today's date as YYYY-MM-DD
  const today = new Date();
  const todayFormatted = today.toISOString().split('T')[0];
  
  // Calculate 7 days before today
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);
  const sevenDaysAgoFormatted = sevenDaysAgo.toISOString().split('T')[0];
  
  // Set default values
  setStartDate(sevenDaysAgoFormatted);
  setEndDate(todayFormatted);
}, []);
    
  const fetchPackageAllowance = async () => {
    setLoading1(true); 
    try {
      const response = await fetch(`${API_BASE_URL}/users/package-data/log_download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setPackageAllowance(data === 1);

    } catch (error) {
      console.error('Error fetching user package:', error);
      setError('Failed to fetch user package. Please try again later.');
    } finally {
      setLoading1(false);
    }
  };

  const fetchRecordSummaries = async () => {
    setLoading2(true);
    try {
      const response = await fetch(`${API_BASE_URL}/traffic/data-devices`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setDevices(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching presets:', error);
      setError('Failed to fetch presets. Please try again later.');
    } finally {
      setLoading2(false);
    }
  };

  const fetchChart = async () => {
    setLoading3(true);
    try {
      const response = await fetch(`${API_BASE_URL}/traffic/data-chart-data?timeRange=${timeRange}&device=${selectedDevice}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setChartData(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching traffic summaries:', error);
      setError('Failed to fetch traffic summaries. Please try again later.');
    } finally {
      setLoading3(false);
    }
  };

  const handleFormatChange = (event) => {
    setExportFormat(event.target.value);
  };

  const handleExport = async (name, exportFunction) => {

    setExportName(name);
    setExportLoading(true);
    
    try {
      await exportFunction();
    } catch (error) {
      console.error(`${name} export failed:`, error);
    } finally {
      setExportLoading(false);
    }

  };
  
  const handleDownloadLogs = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/traffic/export-logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          devices: selectedDevices,
          startDate: startDate,
          endDate: endDate,
          format: exportFormat
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `lss-detailed-logs-${startDate}-${endDate}.zip`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          a.remove();
        }, 100);
      } else {
        const errorText = await response.text();
        console.error('Download failed with status:', response.status, errorText);
        alert(`Download failed: ${response.status} - ${errorText || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Download failed:', error);
      alert(`Download failed: ${error.message}`);
    }
  };

  const handleDownloadSummarizedLogs = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/traffic/export-summarized-logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          devices: selectedDevices,
          startDate: startDate,
          endDate: endDate,
          format: exportFormat
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `lss_summarized_${selectedDevices.join('_')}_${startDate}_${endDate}.${exportFormat}`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          a.remove();
        }, 100);
      }
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleDownloadSummarizedMonthlyLogs = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/traffic/export-summarized-monthly-logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          devices: selectedDevices,
          startDate: startDate,
          endDate: endDate,
          format: exportFormat
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `lss_summarized_${selectedDevices.join('_')}_${startDate}_${endDate}.${exportFormat}`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          a.remove();
        }, 100);
      }
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleDownloadCustomLogs1 = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/traffic/export-custom-logs-1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          devices: selectedDevices,
          startDate: startDate,
          endDate: endDate,
          format: exportFormat
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `lss_custom1_${selectedDevices.join('_')}_${startDate}_${endDate}.${exportFormat}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (error) {
      console.error('Download failed:', error);
    }
  };


  useEffect(() => {
    fetchChart();
  }, [timeRange, selectedDevice]);


  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'primaryColor') {
        setPrimaryColor(event.newValue || '#0f0f0f');
      } else if (event.key === 'secondaryColor') {
        setSecondaryColor(event.newValue || '#1a1a1a');
      } else if (event.key === 'darkMode') {
        setDarkMode(event.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);

    const handleColorChange = () => {
      setPrimaryColor(localStorage.getItem('primaryColor') || '#0f0f0f');
      setSecondaryColor(localStorage.getItem('secondaryColor') || '#1a1a1a');
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    };

    const intervalId = setInterval(handleColorChange, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, []);

  const CardSkeleton = () => (
    <div className="dashboard-card skeleton-card" style={{ backgroundColor: secondaryColor }}>
      <div className="dashboard-card-main">
        <div className="skeleton-title"></div>
        <div className="skeleton-value"></div>
        <div className="skeleton-text"></div>
      </div>
    </div>
  );

  const ChartSkeleton = () => (
    <div className="dashboard-card skeleton-chart" style={{ backgroundColor: secondaryColor }}>
      <div className="skeleton-title"></div>
      <div className="skeleton-chart-area"></div>
    </div>
  );

  const TableSkeleton = () => (
    <div className="dashboard-card skeleton-table" style={{ backgroundColor: secondaryColor }}>
      <div className="skeleton-title"></div>
      <div className="skeleton-rows">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="skeleton-row"></div>
        ))}
      </div>
    </div>
  );

  const getChartData = () => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    
    return {
      labels: timeRange === 'daily' 
          ? Array.from({length: 24}, (_, i) => `${i.toString().padStart(2, '0')}:00`)
          : Array.from({length: timeRange === 'weekly' ? 7 : 30}, (_, i) => {
              const date = new Date(yesterday);
              date.setDate(yesterday.getDate() - i);
              return timeRange === 'weekly'
                  ? date.toLocaleDateString('en-US', { weekday: 'short' })
                  : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          }).reverse(),
      datasets: [
          {
              label: 'Total Traffic',
              data: chartData.map(item => item.total),
              borderColor: '#4CAF50',
              backgroundColor: 'rgba(76, 175, 80, 0.3)',
              tension: 0.4,
              fill: {
                  target: 'origin',
                  above: 'rgba(76, 175, 80, 0.3)'
              }
          },
          {
              label: 'Speeding',
              data: chartData.map(item => item.speeding),
              borderColor: '#F44336',
              backgroundColor: 'rgba(244, 67, 54, 0.3)',
              tension: 0.4,
              fill: {
                  target: '-1',
                  above: 'rgba(244, 67, 54, 0.3)'
              }
          }
      ]
    };
  };
  
  const trafficChartData = {
    labels: getChartData().labels,
    datasets: getChartData().datasets
  };
  
  const trafficChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: getChartData().title,
        color: darkMode ? '#CCCCCC' : '#666666',
        font: { size: 14 }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)',
        titleColor: darkMode ? '#FFFFFF' : '#000000',
        bodyColor: darkMode ? '#FFFFFF' : '#000000',
        borderColor: '#4CAF50',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            return `Records: ${context?.parsed?.y?.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: darkMode ? '#CCCCCC' : '#666666' }
      },
      y: {
        grid: { color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' },
        ticks: { color: darkMode ? '#CCCCCC' : '#666666' }
      }
    }
  };

  const screenStatusOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: () => {
      navigate('/overview');
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          color: darkMode ? '#FFFFFF' : '#000000',
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12
          }
        },
        onClick: () => null
      }
    },
    cutout: '50%',
    radius: '90%',
    hoverOffset: 10
  };
  
  const screenStatusData = {
    labels: ['Online', 'Power Saving', 'Offline'],
    datasets: [{
      data: [
        operatingStatus.running,
        operatingStatus.powerSaving,
        operatingStatus.offline
      ],
      backgroundColor: [
        '#4CAF50', '#FFC107', '#F44336'
      ],
      borderWidth: 0,
      cutout: '70%',
      radius: '90%'
    }]
  };
  

  const DashboardCard = ({ title, total, speeding }) => (
    <div className="dashboard-card" style={{ backgroundColor: secondaryColor }}>
      <div style={{ backgroundColor: secondaryColor, padding: '20px', borderRadius: '8px' }}>
        <div style={{ marginBottom: '20px' }}>
          <Typography variant="h6">{title} Traffic Records</Typography>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div>
              <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>Total</Typography>
              <Typography variant="h4">{total?.toLocaleString()}</Typography>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', color: '#F44336' }}>
            <div>
              <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>Speeding</Typography>
              <Typography variant="h4">{speeding?.toLocaleString()}</Typography>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <Box sx={{ 
        padding: 3, 
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
        boxSizing: 'border-box'
      }}>
      {/* Logo Section */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <img src={LEDSSLogo} alt="LED Speed Signs Logo" style={{ maxWidth: '200px' }} />
      </Box>

      <Modal
        open={exportLoading}
        onClose={() => {}}
        aria-labelledby="export-loading-modal"
        aria-describedby="export-loading-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          textAlign: 'center'
        }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography id="export-loading-modal" variant="h6" component="h2" sx={{ mb: 2 }}>
            Processing {exportName}
          </Typography>
          <Typography id="export-loading-description" sx={{ mb: 3, color: 'text.secondary' }}>
            Please do not refresh or close this page while the file is being generated.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            This may take a moment depending on the amount of data...
          </Typography>
        </Box>
      </Modal>

      {/* Export Traffic Logs Section */}
      <Box sx={{ 
        backgroundColor: '#f5f5f5', 
        borderRadius: 2, 
        p: 3,
        boxShadow: 1
      }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>Export Traffic Logs</Typography>
        <div style={{ 
            display: 'flex',
            gap: '10px'
        }}>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <TextField
                label="Search Devices"
                variant="outlined"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderColor: '#ccc'
                    },
                    '&:hover fieldset': {
                      borderColor: '#999'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(0, 0, 0, 0.6)'
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#1976d2'
                  },
                  '& .MuiOutlinedInput-input': {
                    backgroundColor: 'white',
                    color: 'black !important'
                  }
                }}
              />
              <Box sx={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto', my: 2 }}>
                {!devices ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  devices
                    ?.filter(device => 
                      device.device_name.toLowerCase().includes(searchQuery.toLowerCase())
                      || device.street_address.toLowerCase().includes(searchQuery.toLowerCase())
                      || device.suburb.toLowerCase().includes(searchQuery.toLowerCase())
                      || device.direction.toLowerCase().includes(searchQuery.toLowerCase())
                      || device.description.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    ?.map((device, index) => (
                      <Box key={device.id} sx={{ 
                        my: 1,
                        backgroundColor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.05)' : 'rgba(0, 0, 0, 0.02)'
                      }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedDevices.includes(device.device_name)}
                              onChange={(e) => {
                                if (e.target.checked && selectedDevices.length >= 20) {
                                  alert('Maximum of 20 devices can be selected for export at a time.');
                                  return;
                                }
                                setSelectedDevices(prev => 
                                  e.target.checked 
                                  ? [...prev, device.device_name] 
                                  : prev.filter(id => id !== device.device_name)
                                );
                              }}
                            />
                          }
                            label={
                              <Box component="span">
                                <Box component="span" sx={{ fontWeight: 'bold' }}>{device.device_name}</Box>
                                <br />
                                {device.street_address}, {device.suburb}, {device.direction}
                                <br />
                                Description: {device.description}
                              </Box>
                            }                         
                             sx={{ width: '100%', m: 0, color: 'rgba(0, 0, 0, 0.8)' }}
                        />
                      </Box>
                    ))
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Divider sx={{ my: 1 }}>
                  <Typography variant="body2" sx={{ px: 1, color: 'text.secondary' }}>
                    Date Range
                  </Typography>
                </Divider>
                  <TextField
                    label="Start Date"
                    type="date"
                    variant="outlined"
                    fullWidth
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        '& fieldset': {
                          borderColor: '#ccc'
                        },
                        '&:hover fieldset': {
                          borderColor: '#999'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2'
                        }
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.6)'
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#1976d2'
                      },
                      '& .MuiOutlinedInput-input': {
                        backgroundColor: 'white',
                        color: 'black !important'
                      }
                    }}
                  />

                  <TextField
                    label="End Date"
                    type="date"
                    variant="outlined"
                    fullWidth
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        '& fieldset': {
                          borderColor: '#ccc'
                        },
                        '&:hover fieldset': {
                          borderColor: '#999'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2'
                        }
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.6)'
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#1976d2'
                      },
                      '& .MuiOutlinedInput-input': {
                        backgroundColor: 'white',
                        color: 'black !important'
                      }
                    }}
                  />
                </Box>
                {!packageAllowance && (
                  <Typography variant="body2" sx={{ color: '#ff4444', mt: 1, textAlign: 'center' }}>
                      Log downloads disabled by current subscription. Contact support to upgrade.
                  </Typography>
                )}
                <Divider sx={{ my: 1 }}>
                  <Typography variant="body2" sx={{ px: 1, color: 'text.secondary' }}>
                    Export Format
                  </Typography>
                </Divider>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl>
                    <RadioGroup 
                      value={exportFormat} 
                      onChange={handleFormatChange}
                      sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
                    >
                      <FormControlLabel 
                        value="csv" 
                        control={<Radio />} 
                        label="CSV" 
                        sx={{ color: 'black' }}
                      />
                      <FormControlLabel 
                        value="xlsx" 
                        control={<Radio />} 
                        label="Excel" 
                        sx={{ color: 'black' }}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
                <Divider sx={{ my: 1 }}>
                  <Typography variant="body2" sx={{ px: 1, color: 'text.secondary' }}>
                    Date Type
                  </Typography>
                </Divider>
                <Button
                  variant="contained"
                  onClick={() => handleExport('Summarized Logs', handleDownloadSummarizedLogs)}
                  disabled={selectedDevices.length === 0 || !startDate || !endDate || !packageAllowance}
                  sx={{
                    width: '100%',
                    backgroundColor: '#4CAF50',
                    '&:hover': { backgroundColor: '#45a049' }
                  }}
                >
                  EXPORT SUMMARIZED LOGS (DAILY)
                </Button>

                <Button
                  variant="contained"
                  onClick={() => handleExport('Summarized Logs', handleDownloadSummarizedMonthlyLogs)}
                  disabled={selectedDevices.length === 0 || !startDate || !endDate || !packageAllowance}
                  sx={{
                    width: '100%',
                    backgroundColor: '#4CAF50',
                    '&:hover': { backgroundColor: '#45a049' }
                  }}
                >
                  EXPORT SUMMARIZED LOGS (MONTHLY)
                </Button>

                <Button
                  variant="contained"
                  onClick={() => handleExport('Detailed Logs', handleDownloadLogs)}
                  disabled={selectedDevices.length === 0 || !startDate || !endDate || !packageAllowance}
                  sx={{
                    width: '100%',
                    backgroundColor: '#4CAF50',
                    '&:hover': { backgroundColor: '#45a049' }
                  }}
                >
                  EXPORT DETAILED LOGS
                </Button>

                <Button
                  variant="contained"
                  onClick={() => handleExport('Custom Logs 1', handleDownloadCustomLogs1)}
                  disabled={selectedDevices.length === 0 || !startDate || !endDate || !packageAllowance}
                  sx={{
                    width: '100%',
                    backgroundColor: '#4CAF50',
                    '&:hover': { backgroundColor: '#45a049' }
                  }}
                >
                  EXPORT CUSTOM LOGS 1
                </Button>
            </Box>
        </div>
      </Box>
    </Box>
    </div>
    
  );
};

export default DataDashboard;